// Submits a catering quote to the live BTW NYC system used by btwnyc.com / Fresh Fast Foods.
// Endpoint discovered + verified live: POST https://btwnyc.com/api/catering
// (apex domain — `www.btwnyc.com` redirects 308 to apex; browsers don't always
// re-send the POST body across a 308, which surfaces as "submit failed" on
// Safari/iOS. Posting straight to the apex avoids the redirect entirely.)
// CORS is open (* on Access-Control-Allow-Origin) and explicitly allow-lists the
// Emergent preview origin on preflight. No auth header required.
//
// BTW schema (only these fields are persisted server-side):
//   name, email, phone, event_date, event_time, guests, staffing, message
//
// The form on this site collects more fields than BTW stores (organization, event
// location, event type, package interest, budget, wants_menu). We fold those into
// the message body with a SOURCE TAG so Sam's polling job on btwnyc.com can:
//   1. Identify Fresh Fast Foods catering quotes vs. native BTW quotes
//   2. Recover the package / event type / budget / etc. context
//
// Detection contract for Sam (Wingman / WhatsApp notifier):
//   Poll: GET https://btwnyc.com/api/catering
//   New submission appears with `created_at` >= last_poll AND
//   `message` starts with the SOURCE_TAG constant below.

const BTW_ENDPOINT = "https://btwnyc.com/api/catering";
export const SOURCE_TAG = "[FreshFastFoodsLLC.com Catering Site]";

const fmtField = (label, value) => {
  if (value === null || value === undefined) return null;
  const v = typeof value === "boolean" ? (value ? "Yes" : "No") : String(value).trim();
  if (!v) return null;
  return `${label}: ${v}`;
};

export const buildBtwPayload = (v) => {
  const ffMessage = (v.message || "").trim();

  // Compose a structured message body with the source tag + FFL-only fields.
  const extraLines = [
    fmtField("Organization", v.organization),
    fmtField("Event Type", v.eventType),
    fmtField("Event Location", v.eventLocation),
    fmtField("Package Interest", v.packageInterest),
    fmtField("Budget Range", v.budget),
    fmtField("Requested Catering Menu", v.wantsMenu),
  ].filter(Boolean);

  const composedMessage = [
    SOURCE_TAG,
    "",
    ...extraLines,
    extraLines.length && ffMessage ? "" : null,
    ffMessage ? `Notes from guest:\n${ffMessage}` : null,
  ]
    .filter((x) => x !== null)
    .join("\n");

  return {
    name: v.fullName.trim(),
    email: v.email.trim(),
    phone: v.phone.trim(),
    event_date: v.eventDate || "",
    event_time: v.eventTime || "",
    guests: v.guestCount ? String(v.guestCount).trim() : "",
    staffing: v.serviceStyle || "",
    message: composedMessage,
  };
};

/**
 * Submit the quote to the BTW NYC system.
 * @returns {Promise<{ok: true, data: object} | {ok: false, error: string}>}
 */
export const submitQuoteToBtw = async (formValues) => {
  const payload = buildBtwPayload(formValues);

  try {
    const res = await fetch(BTW_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      let detail = `HTTP ${res.status}`;
      try {
        const j = await res.json();
        if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
      } catch {
        // ignore non-JSON error bodies
      }
      return { ok: false, error: detail };
    }

    const data = await res.json();
    // Sanity check: BTW returns at least { id, quote_token } on success
    if (!data || (!data.id && !data.quote_token)) {
      return { ok: false, error: "Unexpected response from quote system" };
    }
    return { ok: true, data };
  } catch (err) {
    return { ok: false, error: err?.message || "Network error" };
  }
};
